import {ListAltOutlined as EmptyIcon} from '@mui/icons-material';
import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  Link,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import React, {useMemo} from 'react';
import {trackEvent} from '@app/analytics.js';
import CollectionsAutocomplete from '@app/components/Collections/CollectionsAutocomplete';
import {getHashFromId} from '@app/util/granero';

const isUsed = (prop, config) => {
  return config.titleProperty === prop || config.descriptionProperty === prop;
};

const ConfigurationStep = ({
  defaultCollectionTitle,
  onPropertyOptionsChanged,
  onSelect,
  properties,
  fidProperties,
  propertyOptions,
  selected,
}) => {
  const changed = (prop, ev) => {
    onSelect({[prop]: ev.target.checked});
  };

  const propertyMenus = featureProperties => [
    <MenuItem key="_" value="">
      -- Unset --
    </MenuItem>,
    ...(featureProperties ?? []).map(name => (
      <MenuItem key={name} value={name}>
        {name}
      </MenuItem>
    )),
  ];

  const optionsChanged = (field, value) => {
    const config = {...propertyOptions, [field]: value};
    // value could be blank to "unset", but if in use, select the property
    if (value && isUsed(value, config)) {
      onSelect({[value]: true});
    }
    onPropertyOptionsChanged(config);
  };

  const handleChangeCollection = collection => {
    const collectionOptions = {
      ...(collection ?? {}),
      id: collection?.id ?? 'new',
      title: collection?.title ?? '',
      target:
        collection?.id && collection.id !== 'new'
          ? getHashFromId(collection?.id)
          : undefined,
    };

    trackEvent('Async Upload', {
      action: 'selected collection',
      collectionId: collectionOptions.id,
    });

    onPropertyOptionsChanged({
      ...propertyOptions,
      collection: collectionOptions,
    });
  };

  const selectedCount = useMemo(
    () => Object.values(selected).filter(v => v).length,
    [selected]
  );

  const bulkSelect = selectedCount < properties.length;

  const onBulkSelection = () => {
    const state = {};
    properties.forEach(prop => {
      state[prop] = bulkSelect || isUsed(prop, propertyOptions);
    });
    onSelect(state);
  };

  const hasProperties = (properties ?? []).length > 0;

  const existingCollection =
    !!propertyOptions.collection?.id && propertyOptions.collection.id !== 'new';

  return (
    <Stack sx={{width: '100%', height: '100%'}}>
      <Stack
        direction={{sm: 'column', md: 'row'}}
        sx={{
          width: '100%',
          height: '100%',
        }}
      >
        <Stack
          sx={theme => ({
            gap: 2,
            width: '50%',
            paddingBlockStart: 4,
            paddingBlockEnd: 2,
            paddingInline: 3,
            [theme.breakpoints.down('md')]: {
              width: '100%',
            },
          })}
        >
          <CollectionsAutocomplete
            placeholder={defaultCollectionTitle}
            value={propertyOptions.collection}
            onChange={handleChangeCollection}
          />

          <Typography sx={{marginTop: 2}}>
            Map file properties to selected fields
          </Typography>

          <TextField
            disabled={
              !hasProperties || existingCollection || fidProperties.length === 0
            }
            fullWidth
            helperText="The value of this property will be used for the feature ID."
            label="Feature identifier property"
            onChange={ev => optionsChanged('idProperty', ev.target.value)}
            select
            size="small"
            value={propertyOptions.idProperty}
          >
            {propertyMenus(fidProperties)}
          </TextField>

          <TextField
            disabled={!hasProperties || existingCollection}
            fullWidth
            helperText="The value of this property will be used to label features."
            label="Feature title property"
            onChange={ev => optionsChanged('titleProperty', ev.target.value)}
            select
            size="small"
            value={propertyOptions.titleProperty}
          >
            {propertyMenus(properties)}
          </TextField>

          <TextField
            disabled={!hasProperties || existingCollection}
            fullWidth
            helperText="The value of this property will be used as additional details."
            label="Feature description property"
            select
            size="small"
            onChange={ev =>
              optionsChanged('descriptionProperty', ev.target.value)
            }
            value={propertyOptions.descriptionProperty}
          >
            {propertyMenus(properties)}
          </TextField>
        </Stack>

        <Paper
          elevation={8}
          square
          sx={theme => ({
            boxShadow: 'none',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            height: '100%',
            overflow: 'hidden',
            paddingBlock: 3,
            paddingInline: 3,
            width: '100%',
            [theme.breakpoints.up('md')]: {
              borderLeft: '1px solid',
              borderColor: 'divider',
              width: '50%',
            },
            ...theme.applyStyles('light', {
              backgroundColor: 'grey.100',
            }),
          })}
        >
          {hasProperties ? (
            <>
              <Stack
                direction={{md: 'column', lg: 'row'}}
                sx={theme => ({
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  [theme.breakpoints.down('lg')]: {
                    alignItems: 'flex-start',
                  },
                })}
              >
                <Typography>
                  Select additional properties to import ({selectedCount})
                </Typography>

                <Link
                  component="button"
                  underline="always"
                  onClick={onBulkSelection}
                  color="primary"
                >
                  {bulkSelect ? 'Select all' : 'Deselect all'}
                </Link>
              </Stack>

              <FormGroup
                sx={{
                  overflow: 'auto',
                  flexWrap: 'nowrap',
                  paddingInlineEnd: 3,
                }}
              >
                {properties.map(prop => {
                  const used = isUsed(prop, propertyOptions);
                  return (
                    <FormControlLabel
                      key={prop}
                      title={
                        used
                          ? 'This property will be imported as it is in use'
                          : undefined
                      }
                      control={
                        <Checkbox
                          aria-labelledby="prop-{prop}"
                          onChange={ev => changed(prop, ev)}
                          checked={selected[prop] || false}
                          // if used, cannot be deselected
                          disabled={used}
                        />
                      }
                      label={prop}
                      sx={{marginLeft: 0}}
                    />
                  );
                })}
              </FormGroup>
            </>
          ) : (
            <Stack
              sx={{
                alignItems: 'center',
                gap: 1,
                height: '100%',
                justifyContent: 'center',
              }}
            >
              <EmptyIcon fontSize="large" sx={{color: 'text.secondary'}} />

              <Typography variant="subtitle1" sx={{color: 'text.secondary'}}>
                No properties found in file
              </Typography>
            </Stack>
          )}
        </Paper>
      </Stack>
    </Stack>
  );
};

ConfigurationStep.propTypes = {
  properties: PropTypes.arrayOf(PropTypes.string),
  fidProperties: PropTypes.arrayOf(PropTypes.string),
  selected: PropTypes.objectOf(PropTypes.bool),
  onSelect: PropTypes.func.isRequired,
  propertyOptions: PropTypes.shape({
    idProperty: PropTypes.string,
    titleProperty: PropTypes.string,
    descriptionProperty: PropTypes.string,
    collection: PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string,
      target: PropTypes.string,
    }),
  }),
  onPropertyOptionsChanged: PropTypes.func.isRequired,
  defaultCollectionTitle: PropTypes.string,
};

export default ConfigurationStep;
